"use client";

import React, { useEffect, useState } from "react";
import { initializePaddle } from "@paddle/paddle-js";
import { useRouter } from "next/navigation";
import axios from "axios";
import { generateRandomPassword } from "@litonarefin/utils";
import { handleToastr } from "@litonarefin/utils/handleToastr";
import { signIn } from "next-auth/react";
import { getDecryptData } from "@litonarefin/utils/hashData";
import ProductDetails from "./ProductDetails";

const PaddleCheckout = ({ searchParams = {} }) => {
  const [paddle, setPaddle] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState({});

  const [urlData, setUrlData] = useState({
    bundle: false,
    paddleProductIds: [],
    bundleProductsIds: [],
    freemiusProducts: [],
    lists: [],
    tags: [],
    proTags: [],
  });

  const router = useRouter();

  const getURLData = () => {
    const paramsData = new URL(window.location.href);

    let key = "";
    if (paramsData.search?.[0] == "?") {
      const searchStr = paramsData.search.slice(1);
      const searchQueryArr = searchStr.split("&");
      searchQueryArr?.forEach((item) => {
        if (item?.startsWith("products=")) {
          key = item.split("=")?.[1];
        }
      });
    }

    return key;
  };

  /**
   * Handle Checkout
   */
  const handleCheckout = async ({ items, coupon = "" }) => {
    setIsLoading(true);

    paddle?.Checkout.open({
      items: items, // [{priceId: "", quantity: 1 }]
      discountCode: coupon || null,
      settings: {
        displayMode: "inline",
        // variant: "one-page",
        frameTarget: "jlt-checkout-container",
        frameInitialHeight: "450",
        frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;",
      },
      customData: {
        lists: urlData?.lists,
        tags: urlData?.tags,
        pro_tags: urlData?.proTags,
        bundle: urlData?.bundle,
        bundle_products_ids: urlData?.bundleProductsIds,
        freemius_products: urlData?.freemiusProducts,
      },
    });
  };

  /**
   * Add Full Screen Loader in append body
   * @param {boolean} condition
   */
  const getFullScreenLoader = (condition) => {
    const body = document.body;
    if (condition) {
      const loaderWrapper = document.createElement("div");
      loaderWrapper.classList.add("jlt_pricing_full_overlay_loader");
      body.appendChild(loaderWrapper);
    } else {
      const loaderWrapper = document.querySelector(".jlt_pricing_full_overlay_loader");
      body.removeChild(loaderWrapper);
    }
  };

  /**
   * Handle Payment Action
   * @param {*} event
   * @returns
   */
  const actionHandle = async (event) => {
    setIsLoading(false);
    if (event.data) {
      setProducts(event.data);
    }

    if (!event.name) {
      return;
    }

    switch (event.name) {
      case "checkout.loaded":
        break;
      case "checkout.closed":
        setIsLoading(false);
        break;
      case "checkout.customer.created":
        getFullScreenLoader(true);

        const order = await createOrder(event.data);

        if (order?.order_id) {
          // Login
          const loginRes = await signIn("credentials", {
            temp: true,
            hash: order?.user_hash,
            redirect: false,
          });

          if (loginRes.ok) {
            handleToastr("Login Successfull!", "success");
          }

          // Update Order ID in paddle transition
          const res = await fetch(`/api/transaction`, {
            method: "PATCH",
            body: JSON.stringify({
              order_id: order?.order_id,
              transaction_id: event.data.transaction_id,
              custom_data: event.data.custom_data || {},
            }),
          });
          const result = await res.json();

          getFullScreenLoader(false);
        }
        break;
      case "checkout.payment.selected":
        break;
      case "checkout.discount.applied":
        const updateOrder = await updateOrderStatus({ status: "pending", eventData: event.data });
        break;
      case "checkout.discount.removed":
        // const removeUpdateOrder = await updateOrderStatus("pending", "", "");
        break;
      case "checkout.items.updated":
        break;
      case "checkout.payment.initiated":
        break;
      case "checkout.completed":
        await updateOrderStatus({ status: "completed", eventData: event?.data });
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  /**
   * Create Order
   */
  const createOrder = async (data) => {
    const billing = {
      first_name: "",
      last_name: "",
      email: data?.customer?.email || "",
      company: "",
      country: "",
      city: "",
      postCode: "",
      password: "",
    };

    let lineItems = [
      {
        price_id: data?.items?.[0]?.price_id,
      },
    ];

    if (data?.custom_data?.bundle && data?.custom_data?.bundle_products_ids?.length) {
      lineItems = data?.custom_data?.bundle_products_ids;
    }

    const orderData = {
      order_status: "pending",
      total: data.totals.total,
      tax_total: data?.totals?.tax || 0,
      billing: billing,
      shipping: billing,
      bundle: data?.custom_data?.bundle, // TRUE | FALSE
      line_items: lineItems,
      freemius_product_data: data?.custom_data?.freemius_products,
      coupon_code: data?.discount?.code || "",
      transaction_id: data.transaction_id,
      lists: data?.custom_data?.lists,
      tags: data?.custom_data?.tags,
      payment_method: "sparkle_paddle_checkout_overlay",
      payment_method_title: "Sparkle Paddle Overlay",
      site_source: process.env.NEXT_PUBLIC_SOURCE_SITE,
    };

    const { data: orderCreateData } = await axios.post(
      `${process.env.NEXT_PUBLIC_NEXTJS_SITE_URL}/api/orders/create`,
      orderData
    );

    return orderCreateData;
  };

  /**
   * Update Order Status
   * @param {'pending' | 'completed'} status
   * @param {object} data
   */
  const updateOrderStatus = async ({ status = "pending", eventData = {} }) => {
    const { data } = await axios.put(
      `${process.env.NEXT_PUBLIC_NEXTJS_SITE_URL}/api/orders/paddle-update`,
      {
        gutenberg: true,
        body: {
          order_id: eventData?.custom_data?.order_id,
          trans_id: eventData?.transaction_id,
          coupon_code: eventData?.discount?.code || "",
          status: status,
          email: eventData?.customer?.email,
          paddle_address_id: eventData?.customer?.address?.id,
          pending_tags: eventData?.custom_data?.tags?.[0]
            ? [eventData?.custom_data?.tags?.[0]]
            : [],
          pro_tags: eventData?.custom_data?.pro_tags,
        },
      }
    );

    if (data?.data?.data?.status === "completed") {
      // Freemius Product create User & Assign license
      if (eventData?.custom_data?.bundle) {
        eventData?.custom_data?.freemius_products.forEach(async (product) => {
          const password = generateRandomPassword();

          const res = await fetch(`/api/freemius-bundle-purchase`, {
            method: "POST",
            body: JSON.stringify({
              email: eventData?.customer?.email,
              password,
              product,
            }),
          });

          if (!res.ok) return;

          const result = await res.json();

          // Redirect order received page
          router.push(`/order-received?oid=${eventData?.custom_data?.order_id}`);
        });
      }

      // Redirect order received page
      router.push(`/order-received?oid=${eventData?.custom_data?.order_id}`);
    }
  };

  const getPriceIds = async () => {
    const items = urlData?.paddleProductIds.reduce((acc, curr) => {
      if (curr?.price_id) {
        acc.push({
          priceId: curr.price_id,
          quantity: 1,
        });
      }

      return acc;
    }, []);

    handleCheckout({ items, coupon: searchParams?.coupon });
  };

  useEffect(() => {
    // Initialization Paddle
    initializePaddle({
      environment: process.env.NEXT_PUBLIC_PADDLE_MODE || "production",
      token: process.env.NEXT_PUBLIC_PADDLE_VENDOR_AUTH_CODE,
      eventCallback: actionHandle,
    }).then((paddleInstance) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
      }
    });

    // GET URL DATA
    const urlData = getDecryptData(getURLData());
    setUrlData(urlData || {});
  }, []);

  useEffect(() => {
    if (paddle && typeof window !== "undefined") {
      getPriceIds();
    }
  }, [paddle, searchParams]);

  return (
    <>
      {/* Checkout Container for Paddle */}
      <div className="jlt-checkout-container jlt-p-8" />

      {/* Product Details  */}
      <ProductDetails products={products} paddle={paddle} />
    </>
  );
};

export default PaddleCheckout;
